import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  TableSortLabel,
  IconButton,
  TextField,
  Button,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteConfirmation from "../../../common/DeleteConfirmation";
import CommonLoader from "../../../common/CommonLoader";
import { NotificationManager } from "react-notifications";
import { GetTestFormulas, DeleteTestFormula } from "../../../../services/TestLibService";
import {
  stableSort,
  getComparator,
} from "../../../../../common-utilities/sortingUtils";

const TestFormulasTable = () => {
  const columns = [
    "Formula Id",
    "Input 1",
    "Units Of Precision",
    "Sig figs",
    "Text Result",
    "Record Id",
    "Actions",
  ];
  const columnHeaders = [
    "formulaId",
    "input1",
    "unitsOfPrecision",
    "sigfigs",
    "textresult",
    "recordid",
  ];
  const rowsPerPageOptions = [10, 25, 50, 100, 200];

  const [formulasData, setFormulasData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  const [searchTerm, setSearchTerm] = useState("");
  const [orderBy, setOrderBy] = useState("");
  const [order, setOrder] = useState("asc");
  const [selectedFormulaToDelete, setSelectedFormulaToDelete] = useState(null);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);

  const fetchFormulas = async () => {
    try {
      setLoading(true);
      const response = await GetTestFormulas();
      setFormulasData(response.results);
      setFilteredData(response.results);
    } catch {
      NotificationManager.error("Error fetching formulas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFormulas();
  }, []);

  useEffect(() => {
    const filtered = formulasData.filter((formula) =>
      columnHeaders.some((header) =>
        String(formula[header] ?? "").toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
    setFilteredData(filtered);
    setPage(0);
  }, [searchTerm, formulasData]);

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const openEditTab = (formulaId) => {
    window.open(
      "/vlp/edit-test-formula/" + encodeURIComponent(formulaId.trim()),
      "_blank"
    );
  };

  const handleDeleteClick = (formula) => {
    setSelectedFormulaToDelete(formula.formulaId);
    setIsDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirmationClose = () => setIsDeleteConfirmationOpen(false);

  const handleDeleteConfirmationConfirm = async () => {
    try {
      const response = await DeleteTestFormula(selectedFormulaToDelete);
      if (response.results === true) {
        NotificationManager.success("Test formula deleted successfully");
        setIsDeleteConfirmationOpen(false);
        fetchFormulas();
      } else {
        NotificationManager.error("Error deleting test formula");
      }
    } catch {
      NotificationManager.error("Error deleting test formula");
    }
  };

  const renderCell = (column, formula) => {
    if (column === "textresult") return formula[column] ? "Yes" : "No";
    return formula[column];
  };

  const renderActions = (formula) => (
    <div style={{ minWidth: "120px" }}>
      <IconButton aria-label="edit" style={{ color: "orange" }} onClick={() => openEditTab(formula.formulaId)}>
        <EditIcon />
      </IconButton>
      <IconButton aria-label="view" style={{ color: "green" }} onClick={() => openEditTab(formula.formulaId)}>
        <VisibilityIcon />
      </IconButton>
      <IconButton aria-label="delete" style={{ color: "red" }} onClick={() => handleDeleteClick(formula)}>
        <DeleteIcon />
      </IconButton>
    </div>
  );

  return (
    <>
      <span className="title">Test Formula Management</span>
      <hr />

      <div style={{ display: "flex", marginBottom: "16px", alignItems: "center", gap: "12px" }}>
        <TextField
          label="Search"
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ flex: 1 }}
        />
        <Button
          variant="contained"
          color="primary"
          size="medium"
          startIcon={<AddIcon />}
          style={{ height: "40px", whiteSpace: "nowrap" }}
          onClick={() => window.open("/vlp/add-test-formula", "_blank")}
        >
          New
        </Button>
      </div>

      <Paper style={{ marginRight: "20px" }}>
        <TableContainer>
          <Table sx={{ minWidth: 400 }} size="small">
            <TableHead
              sx={{
                backgroundColor: (theme) => theme.palette.primary.main,
                color: "white",
              }}
            >
              <TableRow>
                {columns.map((column, index) => (
                  <TableCell
                    key={column}
                    onClick={() => columnHeaders[index] && handleRequestSort(columnHeaders[index])}
                    sx={{ cursor: columnHeaders[index] ? "pointer" : "default" }}
                  >
                    {columnHeaders[index] ? (
                      <TableSortLabel
                        active={orderBy === columnHeaders[index]}
                        direction={orderBy === columnHeaders[index] ? order : "asc"}
                        sx={{
                          color: "white !important",
                          "& .MuiTableSortLabel-icon": { color: "white !important" },
                        }}
                      >
                        {column}
                      </TableSortLabel>
                    ) : (
                      column
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {stableSort(filteredData, getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((formula, index) => (
                  <TableRow
                    key={formula.formulaId}
                    sx={{
                      backgroundColor: index % 2 === 0 ? "rgba(0, 0, 0, 0.04)" : "white",
                    }}
                  >
                    {columnHeaders.map((column) => (
                      <TableCell key={column}>{renderCell(column, formula)}</TableCell>
                    ))}
                    <TableCell>{renderActions(formula)}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={rowsPerPageOptions}
          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      <CommonLoader loading={loading} />

      <DeleteConfirmation
        isOpen={isDeleteConfirmationOpen}
        onClose={handleDeleteConfirmationClose}
        onConfirm={handleDeleteConfirmationConfirm}
        title="Delete Confirmation"
        message="Are you sure you want to delete this formula?"
        color="red"
      />
    </>
  );
};

export default TestFormulasTable;
